import { Edge, Graph, Node, NodeView, Rectangle } from "@antv/x6"
import { Observable } from "rxjs"
import isNumber from 'lodash/isNumber'
import { EventBus } from "@/libs/EventBus"
import { LineType, PortTypes } from "../constant"
import { pointRotate } from "../utils"

const tolerance = 30

export function eventToObserverable(graph: Graph, name: any) {
  return new Observable((observer) => {
    const listener = (args) => {
      observer.next(args)
    }
    graph.on(name, listener)

    return () => {
      graph.off(name, listener)
    }
  })
}

export function createCanvasEvent$(graph: Graph, name: string) {
  const container = graph.container
  return new Observable<any>((observer) => {
    const handler = (e: any) => {
      observer.next(e)
    }
    container.addEventListener(name, handler)

    return () => {
      container.removeEventListener(name, handler)
    }
  })
}

export function createDocumentEvent$(name: string) {
  return new Observable<any>((observer) => {
    const handler = (e: any) => {
      observer.next(e)
    }
    document.addEventListener(name, handler, true)

    return () => {
      document.removeEventListener(name, handler)
    }
  })
}

export function createEventBusEvent$(eventBus: EventBus, name: string) {
  return new Observable<any>((observer) => {
    const handler = (e: any) => {
      observer.next(e)
    }
    eventBus.on(name, handler)

    return () => {
      eventBus.off(name, handler)
    }
  })
}

export function rotatePoint(x: number, y: number, center: PositionLike, angle: number) {
  const {x: cx, y: cy} = center
  const radians = angle * Math.PI / 180
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)
  const dx = x - cx
  const dy = y - cy
  const xNew = cx + dx * cos + dy * sin
  const yNew = cy + dy * cos - dx * sin
  return { x: xNew, y: yNew }
}

export function getNodesNearCoordinate(graph: Graph, position: PositionLike, range = 10) {
  const {x, y} = position
  const nodes = graph.getNodes()
  const nearbyNodes = nodes.filter((node) => {
    const angle = node.angle()
    const rect = new Rectangle(x - tolerance, y - tolerance, 2 * tolerance, 2 * tolerance)
    rect.rotate(angle)
    return node.getBBox().intersectsWithRect(rect)
  })

  return nearbyNodes
}

export function setNodePortVisibility(graph: Graph, node: Node, visibility: 'hidden' | 'visible') {
  const view = graph.findViewByCell(node)
  if (!view) return
  const ports = getStreamPortsFormNodeView(view as NodeView)

  ports.forEach((item: any) => {
    item.setAttribute('visibility', visibility)
  })

  const edges = view.container.querySelectorAll('.nozzle-edge')
  edges.forEach((item: any) => {
    if (visibility === 'hidden') {
      delete item.style.display
    } else {
      item.style.display = undefined
    }
  })
}

export function setLinePortsVisibility(graph: Graph, node: Node, visibility: 'hidden' | 'visible') {
  const view = graph.findViewByCell(node)
  if (!view) return
  const ports = getLinePortsFormNodeView(view as NodeView)
  ports.forEach((item: any) => {
    item.setAttribute('visibility', visibility)
  })
}

export function setNodesPortVisibility(graph: Graph, nodes: Node[], visibility: 'hidden' | 'visible') {
  nodes.forEach(item => setNodePortVisibility(graph, item, visibility))
}

export function setAllPortsVisibilityByNodeView(view: NodeView, visibility: 'hidden' | 'visible') {
  const ports = getAllPortsByNodeView(view)
  ports.forEach((item: any) => {
    item.setAttribute('visibility', visibility)
  })
}

function getStreamPortsFormNodeView(view: NodeView) {
  return getAllPortsByNodeView(view as NodeView).filter(item => !isALinePort(item))
}

function getLinePortsFormNodeView(view: NodeView) {
  return getAllPortsByNodeView(view as NodeView).filter(item => isALinePort(item))
}

function getAllPortsByNodeView(view: NodeView) {
  const ports = view.container.querySelectorAll('.x6-port-body')
  return Array.from(ports)
}

export function isALinePort(item: Element) {
  const type = item.getAttribute('port-group')
  return type === PortTypes.LinePort
}

export function isPoint(obj: any) {
  return isNumber(obj.x) && isNumber(obj.y)
}

export function getNodePortPosition(node: Node, portId: string) {
  const port = node.getPort(portId)
  if (!port) return
  const positions = node.getPortsPosition(port.group!)
  const pos = positions[portId]
  if (!pos) return
  const size = node.getSize()
  const {x, y} = node.getPosition()
  const offsetX = pos.position.x;
  const offsetY = pos.position.y;
  const center = {x: size.width / 2, y: size.height / 2}
  const point = pointRotate({x: offsetX, y: offsetY}, center, node.getAngle())

  return {x: x + point.x, y: y + point.y}
}

export function isTemplateLine(edge: Edge) {
  const data = edge.getData();
  return data.type === LineType.Template
}

export function isNotStream(edge: Edge) {
  const data = edge.getData();
  return data.type !== LineType.FlowLine
}

export function isStream(edge: Edge) {
  const data = edge.getData();
  return data.type === LineType.FlowLine
}